import React from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import CustomerCard from '../components/CustomerCard';
import LabelText from '../components/LabelText';
import Layout from '../components/layout/Layout';
import SplitSection from '../components/SplitSection';
import StatsBox from '../components/StatsBox';
import customerData from '../data/customer-data';
import HeroImage from '../svg/HeroImage';
import SvgCharts from '../svg/SvgCharts';

import bookmetadata from './bookmetadata.png';
import importScreenshot from './import.png';
import filepicker from './filepicker.png';
import reviewMode from './reviewmode.png';
import randomQuotes from './randomquotes.png';
import AppStoreButton from '../svg/AppStore';

const Index = () => (
  <Layout>
    <section className="pt-20 md:pt-40">
      <div className="container mx-auto px-8 lg:flex">
        <div className="text-center lg:text-left lg:w-1/2">
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-none">Library Notes</h1>
          <p className="text-xl lg:text-2xl mt-6 font-light">
            Library Notes is a free, open-source iOS app for taking notes about the books you read.
          </p>
          <p className="mt-8 md:mt-12">
            <AppStoreButton />
          </p>
          <p className="mt-4 text-gray-600">you are what you read</p>
        </div>
        <div className="lg:w-1/2">
          <HeroImage />
        </div>
      </div>
    </section>
    <section id="why" className="py-20 lg:pb-40 lg:pt-48">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl lg:text-5xl font-semibold">
          Why use a special app for notes about books?
        </h2>
        <div className="flex flex-col sm:flex-row sm:-mx-3 mt-12">
          <div className="flex-1 px-3">
            <Card className="mb-8">
              <p className="font-semibold text-xl">Permanent Value</p>
              <p className="mt-4">
                If you are a book lover, you can build up a reading log and notes over the course of
                decades. Library Notes uses simple plain-text markup so your notes will be readable
                forever, by almost any application.
              </p>
            </Card>
          </div>
          <div className="flex-1 px-3">
            <Card className="mb-8">
              <p className="font-semibold text-xl">Personal Ownership</p>
              <p className="mt-4">
                Your book notes are yours. You shouldn’t lose them when a software company goes out
                of business. Library Notes works with simple files! No account, no sign-up, and you
                can move or copy your files wherever you want. You can use file synchronization
                services like iCloud Drive or Dropbox to keep your content in sync across multiple
                devices.
              </p>
            </Card>
          </div>
          <div className="flex-1 px-3">
            <Card className="mb-8">
              <p className="font-semibold text-xl">Perusability</p>
              <p className="mt-4">
                The joy of writing book notes is rereading them! It’s like meeting old friends
                again. Library Notes already has innovative features to help you get reacquainted
                with what you’ve put in your notes.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
    <section id="features" className="py-20">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl lg:text-5xl font-semibold">Features</h2>
      </div>
    </section>
    <SplitSection
      reverseOrder
      primarySlot={
        <div className="lg:pr-32 xl:pr-48 px-4">
          <h3 className="text-3xl font-semibold leading-tight">Markdown-formatted notes</h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            Library Notes is more than a book cataloging application! Many of its features work best
            if you keep <b>notes</b> about what you read. To maximize portability, Library Notes
            uses plain-text notes and you can organize your notes and your books with{' '}
            <span className="font-mono">#hashtags</span>.
          </p>
        </div>
      }
      secondarySlot={<HeroImage />}
    />
    <SplitSection
      primarySlot={
        <div className="lg:pr-32 xl:pr-48">
          <h3 className="text-3xl font-semibold leading-tight">All of your notes in one file</h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            Library Notes is a <i>file-based</i> application. You can keep notes on an entire
            library in a single file, which you can copy / share / backup as you want. You can use a
            service like iCloud Documents to easily synchronize your library contents to all of your
            devices.
          </p>
        </div>
      }
      secondarySlot={
        <img className="w-2/4 mx-auto" src={filepicker} alt="Screenshot of the file picker" />
      }
    />
    <SplitSection
      reverseOrder
      primarySlot={
        <div className="lg:pr-32 xl:pr-48">
          <h3 className="text-3xl font-semibold leading-tight">
            Get book data from Google Books and Open Library
          </h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            Get a head start on adding book metadata with integrated search of Google Books and Open
            Library.
          </p>
        </div>
      }
      secondarySlot={
        <img className="w-2/4 mx-auto" src={bookmetadata} alt="Editing book metadata" />
      }
    />
    <SplitSection
      primarySlot={
        <div className="lg:pr-32 xl:pr-48">
          <h3 className="text-3xl font-semibold leading-tight">
            Import from Goodreads and LibraryThing
          </h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            If you already use Goodreads or LibraryThing to catalog your books, you can easily
            import your book catalog to Library Notes.
          </p>
        </div>
      }
      secondarySlot={
        <img
          className="w-2/4 mx-auto"
          src={importScreenshot}
          alt="Screenshot of the import dialog"
        />
      }
    />
    <SplitSection
      reverseOrder
      primarySlot={
        <div className="lg:pl-32 xl:pl-48">
          <h3 className="text-3xl font-semibold leading-tight">Review Mode</h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            If you capture quotes, question-and-answer information, or fill-in-the-blank challenges
            in your notes, Library Notes will present them to you in <i>Review Mode</i>.
          </p>
        </div>
      }
      secondarySlot={
        <img className="w-2/4 mx-auto" src={reviewMode} alt="Library Notes Review Mode" />
      }
    />
    <SplitSection
      primarySlot={
        <div className="lg:pr-32 xl:pr-48">
          <h3 className="text-3xl font-semibold leading-tight">Random Quotes</h3>
          <p className="mt-8 text-xl font-light leading-relaxed">
            Sometimes all you need is a selection of inspiring, thought-provoking quotes from the
            books you've read. Library Notes delivers!
          </p>
        </div>
      }
      secondarySlot={
        <img className="w-2/4 mx-auto" src={randomQuotes} alt="Library Notes Random Quotes" />
      }
    />
    <section id="legal">
      <div className="container mx-auto text-center py-20">
        <h2 className="text-3xl lg:text-5xl font-semibold">Legal Stuff</h2>
      </div>
      <div className="container mx-auto prose px-4">
        <p>
          The quick summary of the this section: I'm not trying to turn Library Notes into a
          business. I wrote this software because I love books, I've been keeping notes on the books
          I've read off and on for the past twenty five years, and I think this software I've
          developed for my own use might be useful to other book lovers.
        </p>
        <h3 className="text-lg font-semibold">Terms and Conditions</h3>
        <p>
          Library Notes is free, open-source software provided under the{' '}
          <a href="https://opensource.org/licenses/Apache-2.0">Apache 2.0</a> license. You can
          access the source code <a href="https://github.com/bdewey/LibraryNotes">here</a>.
        </p>
        <h3 className="text-lg font-semibold">Privacy Policy</h3>
        <p>
          Your notes are kept in files that remain under your control. Your notes are never stored
          on Library Notes servers, because <i>Library Notes</i> servers don't even exist. Library
          Notes does not provide any mechanism for note storage or synchronization; however, you may
          choose to use services such as iCloud or Dropbox to synchronize your note files.
        </p>
        <p>Library Notes does not track you. It does not provide any information to advertisers.</p>
        <p>
          If you opt in to crash log reporting with Apple, then I will get copies of crash reports.
          Crash reports are stored privately and kept confidential.
        </p>
      </div>
    </section>
  </Layout>
);

export default Index;
